import { useState, useEffect } from 'react';
import API, { fmt, today } from '@/lib/api';
import { Plus, LinkSimple, LinkBreak, X, CloudArrowDown, CheckCircle, Clock, Warning, CurrencyEur, MagnifyingGlass, Bell, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { BankStatementUpload } from '@/components/BankStatementUpload';
import Pagination, { usePagination } from '@/components/Pagination';

const BADGE_MAP = { gekoppeld: 'badge-green', ongekoppeld: 'badge-yellow' };
const MATCH_LABELS = { ogm: 'OGM', factuurnummer: 'Factuurnr.', auto: 'Bedrag', manueel: 'Manueel', bedrag_en_referentie:'Bedrag + referentie' };

export default function BankPage() {
  const [bank, setBank] = useState([]);
  const [docs, setDocs] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [koppelId, setKoppelId] = useState(null);
  const [facId, setFacId] = useState('');
  const [form, setForm] = useState({ datum: today(), bedrag: '', van: '', meded: '' });
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [filter, setFilter] = useState('alle');
  const [remindingBusy, setRemindingBusy] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkFacId, setBulkFacId] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = () => {
    API.get('/bank').then(r => { setBank(r.data); setSelected(s => s.filter(id => (r.data || []).some(v => v.id === id))); }).catch(() => {});
    API.get('/documenten').then(r => setDocs(r.data)).catch(() => {});
    API.get('/inkomende-documenten').then(r=>setIncoming(r.data)).catch(()=>{});
  };
  useEffect(load, []);

  const toggleSel = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const bulkVerwijder = async () => {
    const gekoppeld = selected.filter(id => bank.find(v => v.id === id)?.fac_id).length;
    const extra = gekoppeld ? `\n\n${gekoppeld} ervan zijn gekoppeld aan een factuur — die facturen worden opnieuw als (deels) onbetaald gezet.` : '';
    if (!window.confirm(`${selected.length} verrichting(en) definitief verwijderen?${extra}`)) return;
    setBulkBusy(true);
    try {
      const r = await API.post('/bank/bulk-verwijderen', { ids: selected });
      toast.success(`${r.data.verwijderd} verrichting(en) verwijderd`);
      setSelected([]); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk verwijderen mislukt'); }
    finally { setBulkBusy(false); }
  };

  const bulkKoppel = async () => {
    if (!bulkFacId) { toast.error('Selecteer eerst een factuur'); return; }
    setBulkBusy(true);
    try {
      const r = await API.post('/bank/bulk-koppel', { ids: selected, ...(bulkFacId.startsWith('ink:')?{inkomend_doc_id:bulkFacId.slice(4)}:{fac_id:bulkFacId}) });
      toast.success(`${r.data.gekoppeld} verrichting(en) gekoppeld aan ${r.data.fac_id}`);
      if(r.data.fouten?.length)toast.warning(`${r.data.fouten.length} niet gekoppeld: ${r.data.fouten[0].reden}`);
      setSelected([]); setBulkFacId(''); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk koppelen mislukt'); }
    finally { setBulkBusy(false); }
  };

  const bulkAutomatch = async () => {
    setBulkBusy(true);
    try {
      const r = await API.post('/bank/bulk-automatch', { ids: selected });
      if (r.data.gekoppeld) toast.success(`${r.data.gekoppeld} automatisch gekoppeld${r.data.ongekoppeld ? `, ${r.data.ongekoppeld} zonder match` : ''}`);
      else toast.warning('Geen unieke match op bedrag én referentie — graag nazien');
      setSelected([]); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Automatisch koppelen mislukt'); }
    finally { setBulkBusy(false); }
  };

  const u = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const saveBank = async () => {
    const bedrag = parseFloat(form.bedrag) || 0;
    if (!bedrag) { alert('Vul bedrag in'); return; }
    try {
    const r = await API.post('/bank', { ...form, bedrag });
    setForm({ datum: today(), bedrag: '', van: '', meded: '' });
    setShowForm(false);
    load();
    if (r.data.fac_id) {
      setImportResult({ message: `Automatisch gekoppeld aan ${r.data.fac_id} via ${MATCH_LABELS[r.data.match_type] || 'matching'}`, linked: 1, imported: 1 });
    }
    if(r.data.status==='ongekoppeld')toast.warning(r.data.match_reden||'Niet gematcht — graag nazien');
    }catch(e){toast.error(e.response?.data?.detail||'Toevoegen mislukt');}
  };

  const koppel = async () => {
    if (!facId) { alert('Selecteer factuur'); return; }
    try {
      await API.post(`/bank/${koppelId}/koppel`, facId.startsWith('ink:')?{inkomend_doc_id:facId.slice(4)}:{fac_id:facId});
      setKoppelId(null); setFacId('');load();
      toast.success('Betaling aan factuur gekoppeld');
    }catch(e){toast.error(e.response?.data?.detail||'Koppelen mislukt');}
  };

  const ontkoppel = async (bid) => {
    if (!window.confirm('Weet u zeker dat u deze koppeling wilt ongedaan maken?')) return;
    try{await API.post(`/bank/${bid}/ontkoppel`);load();}catch(e){toast.error(e.response?.data?.detail||'Ontkoppelen mislukt');}
  };

  const verwijder = async (v) => {
    const extra = v.fac_id ? `\n\nDeze verrichting is gekoppeld aan ${v.fac_id} — die factuur wordt opnieuw als (deels) onbetaald gezet.` : '';
    if (!window.confirm(`Verrichting van ${v.datum} (${fmt(v.bedrag)}) definitief verwijderen?${extra}`)) return;
    try {
      await API.delete(`/bank/${v.id}`);
      toast.success('Bankverrichting verwijderd');
      if (koppelId === v.id) setKoppelId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verwijderen mislukt');
    }
  };

  const codaboxImport = async () => {
    setImporting(true); setImportResult(null); setReminderResult(null);
    try {
      const r = await API.post('/bank/codabox-import');
      setImportResult(r.data);
      load();
    } catch (e) {
      setImportResult({ message: 'Import mislukt' });
    }
    setImporting(false);
  };

  const runRemindersNow = async () => {
    if (!window.confirm('Nu automatische herinneringen versturen voor alle openstaande, vervallen facturen?\n\n(Volgens uw ingestelde stappen: 1e herinnering, 2e herinnering, aanmaning — per klant via e-mail/SMS/WhatsApp waar toegestaan.)')) return;
    setRemindingBusy(true);
    try {
      const r = await API.post('/herinneringen/bulk');
      const d = r.data || {};
      setReminderResult({
        ok: true,
        message: d.created > 0
          ? `✓ ${d.created} herinnering(en) aangemaakt · ${d.emails_sent || 0} e-mails · ${d.sms_sent || 0} SMS verzonden`
          : 'Geen herinneringen nodig — alle facturen binnen verval of reeds herinnerd volgens uw schema.',
        count: d.created || 0,
      });
    } catch (err) {
      setReminderResult({ ok: false, message: 'Herinneringen uitvoeren mislukt: ' + (err.response?.data?.detail || err.message) });
    } finally {
      setRemindingBusy(false);
    }
  };

  const openFac = docs.filter(f => f.type === 'factuur' && f.status !== 'betaald');
  const openIncoming=incoming.filter(f=>f.openstaand>0&&f.status_doc!=='afgekeurd'&&f.doc_type!=='creditnota');
  const invoiceOptions=(negative)=>negative?openIncoming.map(f=><option key={f.id} value={`ink:${f.id}`}>{`${f.leverancier||'Leverancier'} · ${f.factuurnummer||f.id} — ${fmt(f.openstaand)}`}</option>):openFac.map(f=><option key={f.id} value={f.id}>{`${f.id} — ${fmt(f.totaal)}`}</option>);
  const ong = bank.filter(v => v.status === 'ongekoppeld').length;
  const totaal = bank.reduce((s, v) => s + (v.bedrag || 0), 0);
  const gek = bank.filter(v => v.status === 'gekoppeld').length;

  const filtered = filter === 'alle' ? bank : bank.filter(v => v.status === filter);
  const {pageSize,setPageSize,curPage,setPage,paged}=usePagination(filtered,'bankverrichtingen',50);
  const allSel = paged.length > 0 && paged.every(v => selected.includes(v.id));
  const toggleAll = () => setSelected(allSel ? selected.filter(id => !paged.some(v => v.id === id)) : [...new Set([...selected, ...paged.map(v => v.id)])]);

  return (
    <div data-testid="bank-page">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h1 className="page-heading mb-0">Bankverrichtingen</h1>
        <div className="flex flex-wrap gap-2">
          <button className="btn btn-primary" onClick={codaboxImport} disabled={importing} data-testid="codabox-import-btn">
            <CloudArrowDown size={16} weight="bold" /> {importing ? 'Importeren...' : 'CodaBox import'}
          </button>
          <BankStatementUpload busy={importing} setBusy={setImporting} onResult={setImportResult} onImported={load} />
          <button className="btn btn-secondary" onClick={() => setShowForm(!showForm)} data-testid="toggle-bank-form">
            {showForm ? <><X size={14} /> Sluiten</> : <><Plus size={14} weight="bold" /> Manueel</>}
          </button>
        </div>
      </div>

      {ong>0 && <button type="button" onClick={()=>setFilter('ongekoppeld')} className="mb-4 w-full border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-left text-sm flex items-center gap-2" data-testid="bank-unmatched-alert"><Warning size={18}/><strong>{ong} niet-gematchte betaling{ong===1?'':'en'}</strong><span className="ml-auto">Nazien →</span></button>}

      {importResult && (
        <div className={`mb-4 p-3 border text-sm ${importResult.error||importResult.niet_gematcht ? 'border-red-200 bg-red-50 text-red-800' : 'border-green-200 bg-green-50 text-green-800'}`} data-testid="import-result">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">{importResult.message}</div>
            <button className="text-gray-400 hover:text-gray-600" onClick={() => setImportResult(null)} data-testid="bank-import-dismiss" aria-label="Importmelding sluiten"><X size={12} /></button>
          </div>
          {/* Na CODA-import: bied aan om voor nog-openstaande facturen onmiddellijk herinneringen te sturen */}
          {openFac.length > 0 && (
            <div className="mt-2 pt-2 border-t border-green-200/60 flex items-center justify-between gap-3">
              <div className="text-[12px] text-gray-700">
                <strong>{openFac.length}</strong> openstaande factu{openFac.length === 1 ? 'ur' : 'ren'} overgebleven na deze import.
                Stuur nu automatisch herinneringen voor wie verval-datum is overschreden.
              </div>
              <button
                type="button"
                onClick={runRemindersNow}
                disabled={remindingBusy}
                className="btn btn-primary btn-sm inline-flex items-center gap-1.5 whitespace-nowrap"
                style={{ background: '#7C3AED', borderColor: '#7C3AED' }}
                data-testid="post-coda-reminders-btn"
                title="Voer /herinneringen/bulk direct uit — op basis van uw ingestelde herinneringsschema"
              >
                <Bell size={14} weight="bold" /> {remindingBusy ? 'Bezig...' : 'Herinneringen nu sturen'}
              </button>
            </div>
          )}
        </div>
      )}

      {reminderResult && (
        <div
          className={`mb-4 p-3 border text-sm ${reminderResult.ok ? (reminderResult.count > 0 ? 'border-purple-200 bg-purple-50 text-purple-800' : 'border-gray-200 bg-gray-50 text-gray-700') : 'border-red-200 bg-red-50 text-red-800'}`}
          data-testid="reminder-result"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 inline-flex items-center gap-2">
              <Bell size={14} weight={reminderResult.count > 0 ? 'fill' : 'regular'} /> {reminderResult.message}
            </div>
            <button className="text-gray-400 hover:text-gray-600" onClick={() => setReminderResult(null)}><X size={12} /></button>
          </div>
        </div>
      )}

      {ong > 0 && <div className="alert-warn mb-4">{ong} verrichting(en) konden niet automatisch gekoppeld worden. Koppel ze handmatig.</div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5 [&>.kpi-card]:min-w-0 [&_.kpi-value]:whitespace-normal [&_.kpi-value]:break-all" data-testid="bank-summary">
        <div className="kpi-card"><div className="kpi-label">Netto verrichtingen</div><div className="kpi-value text-[#008A27]" data-testid="bank-summary-net">{fmt(totaal)}</div></div>
        <div className="kpi-card"><div className="kpi-label">Verrichtingen</div><div className="kpi-value" data-testid="bank-summary-count">{bank.length}</div></div>
        <div className="kpi-card"><div className="kpi-label">Gekoppeld</div><div className="kpi-value text-[#002FA7]" data-testid="bank-summary-linked">{gek}</div></div>
        <div className="kpi-card"><div className="kpi-label">Ongekoppeld</div><div className="kpi-value text-[#E5B300]" data-testid="bank-summary-unmatched">{ong}</div></div>
      </div>

      {showForm && (
        <div className="inline-form mb-4" data-testid="bank-form">
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Datum</label><input type="date" className="field-input" value={form.datum} onChange={u('datum')} data-testid="bank-datum" /></div>
            <div className="form-group"><label className="form-label">Bedrag</label><input type="number" className="field-input" value={form.bedrag} onChange={u('bedrag')} step="0.01" placeholder="0.00" data-testid="bank-bedrag" /></div>
            <div className="form-group form-full"><label className="form-label">Van (tegenpartij)</label><input className="field-input" value={form.van} onChange={u('van')} placeholder="Peeters BV" data-testid="bank-van" /></div>
            <div className="form-group form-full">
              <label className="form-label">Mededeling (OGM of factuurnummer)</label>
              <input className="field-input font-mono" value={form.meded} onChange={u('meded')} placeholder="+++090/9337/55493+++ of FAC-2026-001" data-testid="bank-meded" />
              <p className="text-[10px] text-gray-400 mt-1">Vul de OGM (+++XXX/XXXX/XXXXX+++) of het factuurnummer in voor automatische koppeling</p>
            </div>
          </div>
          <div className="flex gap-2 justify-end mt-3">
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Annuleren</button>
            <button className="btn btn-primary" onClick={saveBank} data-testid="save-bank-btn">Toevoegen</button>
          </div>
        </div>
      )}

      <div className="section-card">
        <div className="section-header">
          <span className="section-title">Verrichtingen</span>
          <div className="flex gap-1">
            {['alle', 'gekoppeld', 'ongekoppeld'].map(f => (
              <button key={f} className={`px-3 py-1 text-xs font-semibold transition-all ${filter === f ? 'bg-[#002FA7] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                onClick={() => setFilter(f)} data-testid={`filter-${f}`}>{f.charAt(0).toUpperCase() + f.slice(1)}</button>
            ))}
          </div>
        </div>
        <div className="section-body">
          {selected.length > 0 && (
            <div className="mb-3 p-2.5 border border-[#002FA7]/30 bg-[#F4F7FF] flex flex-wrap items-center gap-2" data-testid="bank-bulk-bar">
              <span className="text-xs font-bold text-[#002FA7] mr-1" data-testid="bank-bulk-count">{selected.length} geselecteerd</span>
              <select className="field-input text-xs" style={{ maxWidth: 320 }} value={bulkFacId} onChange={e => setBulkFacId(e.target.value)} data-testid="bank-bulk-fac-select">
                <option value="">-- Koppel aan factuur --</option>
                <optgroup label="Uitgaande facturen">{invoiceOptions(false)}</optgroup><optgroup label="Inkomende facturen">{invoiceOptions(true)}</optgroup>
              </select>
              <button className="btn btn-success btn-sm" onClick={bulkKoppel} disabled={bulkBusy || !bulkFacId} data-testid="bank-bulk-koppel-btn">
                <LinkSimple size={14} /> Koppelen
              </button>
              <button className="btn btn-secondary btn-sm" onClick={bulkAutomatch} disabled={bulkBusy} title="OGM / factuurnummer / bedrag opnieuw matchen" data-testid="bank-bulk-automatch-btn">
                <MagnifyingGlass size={14} /> Automatisch koppelen
              </button>
              <button className="btn btn-sm bg-red-600 text-white hover:bg-red-700" onClick={bulkVerwijder} disabled={bulkBusy} data-testid="bank-bulk-delete-btn">
                <Trash size={14} /> Verwijderen
              </button>
              <button className="btn btn-ghost btn-sm ml-auto" onClick={() => setSelected([])} data-testid="bank-bulk-clear-btn"><X size={14} /> Selectie wissen</button>
            </div>
          )}
          <div className="overflow-x-auto"><table className="data-table">
            <thead><tr>
              <th style={{ width: 32 }}><input type="checkbox" checked={allSel} onChange={toggleAll} title="Deze pagina (de)selecteren" data-testid="bank-select-all" /></th>
              <th>Datum</th><th>Van</th><th>Mededeling</th><th className="right">Bedrag</th><th>Status</th><th>Koppeling</th><th>Bron</th><th></th></tr></thead>
            <tbody>
              {paged.map(v => {
                const facDoc = v.fac_id ? docs.find(d => d.id === v.fac_id) : incoming.find(d=>d.id===v.inkomend_doc_id);
                return (
                  <tr key={v.id} className={selected.includes(v.id) ? 'bg-blue-50' : ''} data-testid={`bank-row-${v.id}`}>
                    <td><input type="checkbox" checked={selected.includes(v.id)} onChange={() => toggleSel(v.id)} data-testid={`bank-select-${v.id}`} /></td>
                    <td>{v.datum}</td>
                    <td>{v.van || '-'}</td>
                    <td className="font-mono text-xs">{v.meded || '-'}</td>
                    <td className={`right font-medium ${v.bedrag<0?'text-red-700':'text-[#008A27]'}`} data-testid={`bank-amount-${v.id}`}>{fmt(v.bedrag)}</td>
                    <td><span className={`badge ${BADGE_MAP[v.status] || 'badge-gray'}`} data-testid={`bank-status-${v.id}`}>{v.status==='ongekoppeld'?'Niet gematcht':v.status}</span></td>
                    <td>
                      {(v.fac_id || v.inkomend_doc_id) && (
                        <div className="flex items-center gap-1.5">
                          <CheckCircle size={14} className="text-green-500 shrink-0" weight="bold" />
                          <span className="doc-nr" data-testid={`bank-invoice-${v.id}`}>{v.inkomend_doc_id ? `Inkomend · ${facDoc?.factuurnummer||v.inkomend_doc_id}`:v.fac_id}</span>
                          {v.match_type && <span className="text-[9px] px-1.5 py-0.5 bg-gray-100 text-gray-400 font-semibold">{MATCH_LABELS[v.match_type] || v.match_type}</span>}
                          {facDoc && <span className="text-[10px] text-gray-400">{fmt(facDoc.totaal)}</span>}
                        </div>
                      )}
                      {!v.fac_id && !v.inkomend_doc_id && <span className="text-xs text-red-700 flex items-center gap-1 max-w-xs" data-testid={`bank-reason-${v.id}`}><Clock size={12} />{v.match_reden||'Niet gekoppeld — nazien'}</span>}
                    </td>
                    <td className="text-xs text-gray-400">{v.bron || 'Manueel'}</td>
                    <td>
                      <div className="flex items-center gap-1 justify-end">
                      {v.status === 'ongekoppeld' && koppelId !== v.id && (
                        <button className="btn btn-secondary btn-sm" onClick={() => setKoppelId(v.id)} data-testid={`koppel-btn-${v.id}`}>
                          <LinkSimple size={14} /> Koppelen
                        </button>
                      )}
                      {v.status === 'gekoppeld' && (
                        <button className="btn btn-ghost btn-sm text-gray-400 hover:text-red-500" onClick={() => ontkoppel(v.id)} title="Koppeling ongedaan maken" data-testid={`ontkoppel-btn-${v.id}`}>
                          <LinkBreak size={14} />
                        </button>
                      )}
                      {koppelId === v.id && (
                        <div className="flex gap-2 items-center" data-testid={`koppel-form-${v.id}`}>
                          <select className="field-input text-xs" style={{ maxWidth: 300 }} value={facId} onChange={e => setFacId(e.target.value)} data-testid="koppel-fac-select">
                            <option value="">-- Selecteer factuur --</option>
                            {invoiceOptions(v.bedrag<0)}
                          </select>
                          <button className="btn btn-success btn-sm" onClick={koppel} data-testid="koppel-confirm">Koppelen</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => setKoppelId(null)}>Annul.</button>
                        </div>
                      )}
                      <button className="btn btn-ghost btn-sm text-gray-400 hover:text-red-600 hover:bg-red-50" onClick={() => verwijder(v)} title="Verrichting verwijderen" data-testid={`verwijder-btn-${v.id}`}>
                        <Trash size={14} />
                      </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan="9" className="text-center text-gray-400 py-8">Geen verrichtingen{filter !== 'alle' ? ` met status "${filter}"` : ''}.</td></tr>}
            </tbody>
          </table></div>
          <div className="[&_[data-testid=pagination-bar]>div]:flex-wrap" data-testid="bank-pagination"><Pagination total={filtered.length} pageSize={pageSize} setPageSize={setPageSize} curPage={curPage} setPage={setPage} presets={[]} /></div>
        </div>
      </div>
    </div>
  );
}
